import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { ArrowRight } from 'lucide-react';

/**
 * Composant Hero réutilisable avec image de fond
 * 
 * @param {string} backgroundImage - URL de l'image de fond
 * @param {string} title - Titre principal
 * @param {string} subtitle - Sous-titre
 * @param {string} description - Description optionnelle
 * @param {string} ctaText - Texte du bouton CTA
 * @param {string} ctaLink - Lien du bouton CTA
 * @param {string} height - Hauteur du hero (par défaut: 500px)
 * @param {boolean} overlay - Afficher un overlay sombre (par défaut: true)
 */
const HeroTemplate = ({ 
  backgroundImage = '/templates/page_template.svg',
  title = 'Bienvenue',
  subtitle = '',
  description = '',
  ctaText = 'En savoir plus',
  ctaLink = null,
  height = '500px',
  overlay = true,
  overlayOpacity = 0.5
}) => {
  const navigate = useNavigate();

  const handleCTA = () => {
    // Ne rien faire si pas de lien
    if (!ctaLink || ctaLink === '#') {
      return;
    }
    
    if (ctaLink.startsWith('#') && ctaLink.length > 1) {
      // Scroll vers une section (seulement si le sélecteur est valide)
      const element = document.querySelector(ctaLink);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else if (ctaLink.startsWith('/')) {
      // Navigation interne
      navigate(ctaLink);
    } else {
      // Lien externe
      window.open(ctaLink, '_blank');
    }
  };

  return (
    <div 
      className="relative w-full overflow-hidden"
      style={{ 
        height,
        minHeight: '400px'
      }}
    >
      {/* Image de fond */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ 
          backgroundImage: `url(${backgroundImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        {/* Overlay sombre optionnel */}
        {overlay && (
          <div 
            className="absolute inset-0 bg-black"
            style={{ opacity: overlayOpacity }}
          />
        )}
      </div>

      {/* Contenu */}
      <div className="relative z-10 h-full flex items-center justify-center">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {/* Subtitle en haut */}
          {subtitle && (
            <p className="text-blue-400 font-semibold text-sm sm:text-base uppercase tracking-wide mb-4">
              {subtitle}
            </p>
          )}

          {/* Titre principal */}
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
            {title}
          </h1>

          {/* Description */}
          {description && (
            <p className="text-lg sm:text-xl text-gray-200 mb-8 max-w-2xl mx-auto">
              {description}
            </p>
          )}

          {/* CTA Button */}
          {ctaText && ctaLink && ctaLink !== '#' && (
            <Button
              onClick={handleCTA}
              size="lg"
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-lg font-semibold rounded-lg shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105"
            >
              {ctaText}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          )}
        </div>
      </div>

      {/* Dégradé en bas pour transition douce */}
      <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-slate-50 to-transparent" />
    </div>
  );
};

export default HeroTemplate;
